import React, { useEffect, useState, useRef } from 'react';
import { User, Calendar, ChevronDown, Home, Check } from 'lucide-react';
const LOAN_OFFICERS = ['John Davis', 'Sarah Mitchell', 'Michael Chen', 'Jessica Rodriguez', 'David Thompson', 'Emily Parker'];
const MONTHS = ['January 2025', 'February 2025', 'March 2025', 'April 2025', 'May 2025', 'June 2025', 'July 2025', 'August 2025', 'September 2025', 'October 2025', 'November 2025', 'December 2025'];
interface DropdownProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  icon: React.ReactNode;
  iconRounded?: boolean;
}
function Dropdown({
  label,
  value,
  options,
  onChange,
  icon,
  iconRounded
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);
  return <div className="relative" ref={ref} data-id="element-13">
      <button type="button" onClick={() => setOpen(!open)} aria-haspopup="listbox" aria-expanded={open} aria-label={`${label}: ${value}`} className="flex items-center gap-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-4 py-2 transition-colors min-w-[180px] focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-14">
        <div className={`bg-[#0a1e5e] text-white p-1.5 ${iconRounded ? 'rounded-full' : 'rounded-md'}`} data-id="element-15">
          {icon}
        </div>
        <div className="text-left flex-1" data-id="element-16">
          <p className="text-xs text-gray-500 font-semibold uppercase" data-id="element-17">
            {label}
          </p>
          <p className="text-sm font-bold text-[#0a1e5e]" data-id="element-18">{value}</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-19" />
      </button>

      {open && <ul role="listbox" className="absolute right-0 top-full mt-2 w-full min-w-[220px] bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto py-1" data-id="element-20">
          {options.map(option => {
        const selected = option === value;
        return <li key={option} role="option" aria-selected={selected} tabIndex={0} onClick={() => {
          onChange(option);
          setOpen(false);
        }} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onChange(option);
            setOpen(false);
          }
        }} className={`flex items-center justify-between px-4 py-2 text-sm cursor-pointer hover:bg-blue-50 ${selected ? 'bg-blue-50 text-[#0a1e5e] font-bold' : 'text-gray-700 font-medium'}`} data-id="element-21">
                <span data-id="element-22">{option}</span>
                {selected && <Check className="w-4 h-4 text-blue-600" data-id="element-23" />}
              </li>;
      })}
        </ul>}
    </div>;
}
interface HeaderProps {
  loanOfficer?: string;
  month?: string;
  onLoanOfficerChange?: (value: string) => void;
  onMonthChange?: (value: string) => void;
}
export function Header({
  loanOfficer: controlledLO,
  month: controlledMonth,
  onLoanOfficerChange,
  onMonthChange
}: HeaderProps = {}) {
  const [internalLO, setInternalLO] = useState('John Davis');
  const [internalMonth, setInternalMonth] = useState('May 2025');
  const loanOfficer = controlledLO ?? internalLO;
  const month = controlledMonth ?? internalMonth;
  const handleLO = (val: string) => {
    setInternalLO(val);
    onLoanOfficerChange?.(val);
  };
  const handleMonth = (val: string) => {
    setInternalMonth(val);
    onMonthChange?.(val);
  };
  return <header className="bg-white border-b border-gray-200 px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-24">
      <div className="flex items-center gap-6" data-id="element-25">
        <div className="flex items-center gap-2 text-[#0a1e5e]" data-id="element-26">
          <Home className="w-8 h-8" data-id="element-27" />
          <div data-id="element-28">
            <h1 className="font-bold text-xl leading-tight tracking-tight" data-id="element-29">
              MORTGAGE
            </h1>
            <p className="text-xs font-semibold tracking-widest text-gray-500" data-id="element-30">
              DEPOT
            </p>
          </div>
        </div>
        <div className="hidden md:block h-10 w-px bg-gray-300" data-id="element-31"></div>
        <div data-id="element-32">
          <h2 className="text-3xl font-extrabold text-[#0a1e5e] tracking-tight" data-id="element-33">
            SCORECARD
          </h2>
          <p className="text-sm font-semibold text-blue-600 tracking-widest" data-id="element-34">
            FOCUS. EXECUTE. CLOSE.
          </p>
        </div>
      </div>

      <div className="flex items-center gap-4" data-id="element-35">
        <Dropdown label="Loan Officer" value={loanOfficer} options={LOAN_OFFICERS} onChange={handleLO} icon={<User className="w-4 h-4" data-id="element-37" />} iconRounded data-id="element-36" />
        <Dropdown label="Month" value={month} options={MONTHS} onChange={handleMonth} icon={<Calendar className="w-4 h-4" data-id="element-39" />} data-id="element-38" />
      </div>
    </header>;
}